import { useState } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import { MailCheck, Loader2, Globe, CheckCircle2, Clock, Inbox, MapPin } from "lucide-react";
import { emailStatus, setEmailSender, setEmailPostal } from "../lib/api";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";

// CAN-SPAM: every commercial email needs a valid physical postal address in its footer.
const PostalRow = ({ data }) => {
  const qc = useQueryClient();
  const [editing, setEditing] = useState(false);
  const [value, setValue] = useState(data.postal_address || "");
  const [busy, setBusy] = useState(false);
  const save = async () => {
    setBusy(true);
    try { qc.setQueryData(["email-status"], await setEmailPostal(value)); toast.success(value.trim() ? "Mailing address saved — it now appears in every email footer" : "Mailing address cleared"); setEditing(false); }
    catch (e) { toast.error(e?.response?.data?.detail || "Could not save"); }
    finally { setBusy(false); }
  };
  return (
    <div className="text-xs" data-testid="email-postal">
      <div className="flex items-start gap-1.5">
        <MapPin className="h-3 w-3 text-muted-foreground mt-0.5 shrink-0" />
        <span className="flex-1 min-w-0">Footer mailing address: {data.postal_address ? <span data-testid="email-postal-value">{data.postal_address}</span> : <span className="text-amber-700" data-testid="email-postal-missing">not set — required by CAN-SPAM before newsletters go out</span>}</span>
        {!editing && <button data-testid="email-postal-edit-btn" onClick={() => { setValue(data.postal_address || ""); setEditing(true); }} className="text-primary hover:underline shrink-0">{data.postal_address ? "Change" : "Add"}</button>}
      </div>
      {editing && (
        <div className="flex items-center gap-2 mt-1.5">
          <Input data-testid="email-postal-input" value={value} onChange={(e) => setValue(e.target.value)} placeholder="HearthVault · PO Box 123 · Austin, TX 78701" className="h-9 bg-background text-xs" />
          <Button data-testid="email-postal-save-btn" size="sm" onClick={save} disabled={busy}>{busy ? <Loader2 className="h-4 w-4 animate-spin" /> : "Save"}</Button>
          <Button size="sm" variant="ghost" onClick={() => setEditing(false)}>Cancel</Button>
        </div>
      )}
    </div>
  );
};

export const EmailDeliveryRow = () => {
  const qc = useQueryClient();
  const { data } = useQuery({ queryKey: ["email-status"], queryFn: emailStatus });
  const [editing, setEditing] = useState(false);
  const [value, setValue] = useState("");
  const [busy, setBusy] = useState(false);
  const [showHow, setShowHow] = useState(false);

  const save = async () => {
    setBusy(true);
    try {
      qc.setQueryData(["email-status"], await setEmailSender(value));
      toast.success("Sender address updated — emails now go out from your domain");
      setEditing(false);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not update");
    } finally {
      setBusy(false);
    }
  };

  if (!data) return null;
  return (
    <div className="mt-6" data-testid="email-delivery-section">
      <h4 className="text-xs uppercase tracking-wider font-mono text-muted-foreground flex items-center gap-1.5 mb-2"><MailCheck className="h-3.5 w-3.5" /> Email delivery</h4>
      <div className="rounded-lg border border-border bg-card px-3 py-2.5 text-sm space-y-2">
        <div className="flex items-start justify-between gap-3">
          <div className="min-w-0">
            <div className="font-medium truncate" data-testid="email-from">From: {data.from_address}</div>
            <div className="text-xs text-muted-foreground" data-testid="email-mode">{data.onboarding_mode ? "Onboarding mode — emails only reach your own Resend account address" : "Verified domain — delivers to any family member"}</div>
          </div>
          {data.verified_domains?.length > 0 && !editing && <button data-testid="email-sender-edit-btn" onClick={() => { setEditing(true); setValue(`HearthVault <vault@${data.verified_domains[0]}>`); }} className="text-xs text-primary hover:underline shrink-0">Change</button>}
        </div>
        {editing && (
          <div className="flex items-center gap-2">
            <Input data-testid="email-sender-input" value={value} onChange={(e) => setValue(e.target.value)} className="h-9 bg-background text-xs" />
            <Button data-testid="email-sender-save-btn" size="sm" onClick={save} disabled={busy}>{busy ? <Loader2 className="h-4 w-4 animate-spin" /> : "Save"}</Button>
            <Button size="sm" variant="ghost" onClick={() => setEditing(false)}>Cancel</Button>
          </div>
        )}
        {data.domains.length > 0 && (
          <ul className="space-y-1 text-xs" data-testid="email-domains">
            {data.domains.map((d) => (
              <li key={d.name} className="flex items-center gap-1.5"><Globe className="h-3 w-3 text-muted-foreground" /> {d.name} {d.status === "verified" ? <span className="inline-flex items-center gap-1 text-emerald-700"><CheckCircle2 className="h-3 w-3" /> verified</span> : <span className="inline-flex items-center gap-1 text-amber-700"><Clock className="h-3 w-3" /> {d.status}</span>}</li>
            ))}
          </ul>
        )}
        <div className="text-xs flex items-center gap-1.5" data-testid="email-inbound">
          <Inbox className="h-3 w-3 text-muted-foreground" /> Inbound: {data.inbound_configured ? <span className="text-emerald-700">live · {data.inbound_address}</span> : <span className="text-muted-foreground">not configured (needs RESEND_WEBHOOK_SECRET + INBOUND_ADDRESS)</span>}
        </div>
        <PostalRow data={data} />
        <button data-testid="email-how-btn" onClick={() => setShowHow(!showHow)} className="text-xs text-primary hover:underline">{showHow ? "Hide" : "How to verify a domain"}</button>
        {showHow && <ol className="list-decimal pl-4 text-xs text-muted-foreground space-y-1" data-testid="email-how">{data.how_to.map((h) => <li key={h}>{h}</li>)}</ol>}
      </div>
    </div>
  );
};
